import { useState } from "react";
import { signUploadUrl } from "@/api/uploads";
import type { MediaType, UploadVariant } from "@/interfaces/uploads";

interface UseR2UploadResult {
  upload: (
    file: File,
    mediaType: MediaType,
    campaignId: string,
    variant?: UploadVariant,
  ) => Promise<string>;
  isUploading: boolean;
  uploadError: string | null;
}

export function useR2Upload(): UseR2UploadResult {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function upload(
    file: File,
    mediaType: MediaType,
    campaignId: string,
    variant?: UploadVariant,
  ): Promise<string> {
    setIsUploading(true);
    setUploadError(null);

    try {
      const { presignedUrl, publicUrl } = await signUploadUrl({
        mediaType,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        campaignId,
        variant,
      });

      // PUT directly to R2 — file never touches our server
      const putRes = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      if (!putRes.ok) throw new Error(`Upload to R2 failed: ${putRes.status}`);

      return publicUrl;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setUploadError(msg);
      throw err;
    } finally {
      setIsUploading(false);
    }
  }

  return { upload, isUploading, uploadError };
}
